import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { appMode } from '../config';

export type ThemeName =
  | 'midnight-rose'
  | 'royal-sapphire'
  | 'sunset-glow'
  | 'classroom'
  | 'amethyst'
  | 'amethyst-night'
  | 'amethyst-calm';

interface ThemeContextType {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  availableThemes: { id: ThemeName; name: string; description: string }[];
}

const THEME_STORAGE_KEY = 'gamedate-theme';
// Mode-default theme: classroom mode boots into the classroom skin unless the
// user has an explicit saved choice (localStorage) or a ?theme= URL override.
// WU-01 R5 (human-approved): dating/social scope now defaults to Amethyst.
const DEFAULT_THEME: ThemeName = appMode === 'classroom' ? 'classroom' : 'amethyst';

const availableThemes: ThemeContextType['availableThemes'] = [
  {
    id: 'midnight-rose',
    name: 'Midnight Rose',
    description: 'Deep midnight blue with rose accents',
  },
  {
    id: 'royal-sapphire',
    name: 'Royal Sapphire',
    description: 'Deep sapphire blue with gold accents',
  },
  {
    id: 'sunset-glow',
    name: 'Sunset Glow',
    description: 'Warm purple with coral orange accents',
  },
  {
    id: 'classroom',
    name: 'Quest Academy',
    description: 'Scholarly orange and violet on warm paper',
  },
  {
    id: 'amethyst',
    name: 'Amethyst',
    description: 'Light violet & purple — the V2 default look',
  },
  {
    id: 'amethyst-night',
    name: 'Amethyst Night',
    description: 'Deep violet dark mode with lavender text',
  },
  {
    id: 'amethyst-calm',
    name: 'Amethyst Calm',
    description: 'Violet only — no red or orange, reduced motion',
  },
];

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: ThemeName;
}

export function ThemeProvider({ children, initialTheme }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    // Check for initial theme prop (useful for A/B testing)
    if (initialTheme) return initialTheme;

    // Check localStorage (retired themes fall through to the default)
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'ukrainian-sky') {
        localStorage.setItem(THEME_STORAGE_KEY, 'midnight-rose');
        return 'midnight-rose';
      }
      if (stored && availableThemes.some(t => t.id === stored)) {
        return stored as ThemeName;
      }
    }

    return DEFAULT_THEME;
  });

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);

    // Update body background based on theme
    const root = document.documentElement;
    const bodyBg = getComputedStyle(root).getPropertyValue('--body-background').trim();
    if (bodyBg) {
      document.body.style.background = bodyBg;
    }
  }, [theme]);

  const setTheme = (newTheme: ThemeName) => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, availableThemes }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Helper to get theme from URL param (for A/B testing)
export function getThemeFromUrl(): ThemeName | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const themeParam = params.get('theme');

  if (themeParam && availableThemes.some(t => t.id === themeParam)) {
    return themeParam as ThemeName;
  }

  return null;
}
